'use client';

import { useEffect, useMemo, useState } from 'react';

import { useGlobalContext } from '@/store/contexts/global-context';
import { useStore } from '@/store/store';
import { useIntersectionObserver } from '@/hooks/use-intersection-observer';
import { usePrefersReducedMotion } from '@/hooks/use-prefers-reduced-motion';
import { AMPLITUDE_EXPERIMENTS, getExperimentValue } from '@/utils/experiments';
import {
    ROTATION_INTERVAL_MS,
    ROTATION_MODE,
    pickLaunchIndex,
    resolveMode,
    type Transition
} from './utils/rotating-zone-title-utils';

type RotatingZoneTitleProps = {
    titles: string[];
    zoneKey?: string;
};

export const RotatingZoneTitle = ({ titles, zoneKey }: RotatingZoneTitleProps) => {
    const { experiments } = useGlobalContext();
    const setDisplayedTitle = useStore((state) => state.rotatingZoneTitle.setDisplayedTitle);
    const clearDisplayedTitle = useStore((state) => state.rotatingZoneTitle.clearDisplayedTitle);
    const prefersReducedMotion = usePrefersReducedMotion();
    const [ref, isIntersecting] = useIntersectionObserver<HTMLSpanElement>();
    const [index, setIndex] = useState(0);
    const [transition, setTransition] = useState<Transition | null>(null);

    const variant = useMemo(
        () => getExperimentValue(experiments, AMPLITUDE_EXPERIMENTS.ROTATING_ZONE_TITLE),
        [experiments]
    );
    const mode = useMemo(() => resolveMode(variant, prefersReducedMotion), [variant, prefersReducedMotion]);

    useEffect(() => {
        if (mode !== ROTATION_MODE.LAUNCH) {
            return;
        }

        setIndex(pickLaunchIndex(titles, zoneKey));
    }, [mode, titles, zoneKey]);

    useEffect(() => {
        if (mode !== ROTATION_MODE.TIMED || !isIntersecting) {
            return undefined;
        }
        const intervalId = window.setInterval(() => {
            setIndex((current) => {
                const next = (current + 1) % titles.length;

                setTransition({ from: current });

                return next;
            });
        }, ROTATION_INTERVAL_MS);

        return () => window.clearInterval(intervalId);
    }, [mode, isIntersecting, titles.length]);

    const safeIndex = index % titles.length;

    useEffect(() => {
        if (zoneKey) {
            setDisplayedTitle(zoneKey, titles[safeIndex]);
        }
    }, [zoneKey, titles, safeIndex, setDisplayedTitle]);

    // Clear the published title when this zone stops rotating (unmount, or zoneKey change —
    // e.g. rotation disabled or the header hidden), so analytics readers fall back to the
    // static zone title instead of reporting a stale rotating one.
    useEffect(() => {
        if (!zoneKey) {
            return undefined;
        }

        return () => clearDisplayedTitle(zoneKey);
    }, [zoneKey, clearDisplayedTitle]);

    const outgoing =
        transition && transition.from % titles.length !== safeIndex ? { index: transition.from % titles.length } : null;

    return (
        <span
            ref={mode === ROTATION_MODE.TIMED ? ref : undefined}
            className="rotating-zone-title"
            data-testid="rotating-zone-title">
            {outgoing && (
                <span
                    key={`out-${outgoing.index}`}
                    className="rotating-zone-title__text rotating-zone-title__text--out"
                    aria-hidden="true"
                    onAnimationEnd={() => setTransition(null)}>
                    {titles[outgoing.index]}
                </span>
            )}
            <span
                key={`in-${safeIndex}`}
                className={`rotating-zone-title__text${outgoing ? ' rotating-zone-title__text--in' : ''}`}
                data-testid="rotating-zone-title-value">
                {titles[safeIndex]}
            </span>
        </span>
    );
};
